import React, { useState, useEffect } from "react";
import {
  Flex,
  Grid,
  GridItem,
  IconButton,
  Image,
  Text,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import colors from "../../../utils/colors";
import { pdfjs } from "react-pdf";
import {
  useUploadMedia,
  useAttachMediaToAppointment,
  useGetMediaByAppointment,
  useDeleteMedia,
} from "../../../apis/patients";
import customToast from "../../../components/Toast";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
export interface MediaFile {
  url: any;
  id: string;
  tag?: string;
  label?: string;
  file?: { name: string; type: string };
  previewUrl: string;
}
interface MediaCardProps {
  appointmentId: string;
}
const MediaCard: React.FC<MediaCardProps> = ({ appointmentId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");

  const { mutate: attachMedia } = useAttachMediaToAppointment();
  const { mutate: deleteMedia } = useDeleteMedia();
  const { data, refetch } = useGetMediaByAppointment(appointmentId);

  const mediaFiles = React.useMemo(
    () =>
      Array.isArray(data?.data)
        ? data.data.map((media: any) => ({
            id: media.id || media._id,
            url: media.url,
            tag: media.tag,
            label: media.label,
          }))
        : [],
    [data],
  );

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      newFiles.forEach((file) => {
        attachMedia(
          { appointmentId, file, tag: selectedTag, label: selectedLabel },
          {
            onSuccess: () => {
              refetch();
              setSelectedTag("");
              setSelectedLabel("");
            },
            onError: (error) => {
              console.error("Attach media failed", error);
              customToast("Failed to attach media", "error"); // optional toast
            },
          },
        );
      });
    }
  };

  const handleDeleteMedia = (fileId: string | undefined) => {
    if (!fileId) {
      return;
    }
    deleteMedia(fileId, { onSuccess: refetch });
  };

  const getFileType = (url: string) => {
    if (url.endsWith(".jpg") || url.endsWith(".jpeg") || url.endsWith(".png"))
      return "image/jpeg";
    if (url.endsWith(".pdf")) return "application/pdf";
    if (url.endsWith(".docx"))
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    return "unknown";
  };

  const openMediaPreview = (media: MediaFile) => {
    const decodedUrl = decodeURIComponent(media.url);
    const fileName = decodedUrl.substring(decodedUrl.lastIndexOf("/") + 1);

    setSelectedMedia({
      ...media,
      file: {
        name: fileName || "Unknown File",
        type: getFileType(decodedUrl),
      },
      previewUrl: media.url,
    });

    onOpen();
  };

  return (
    <Flex
      direction="column"
      backgroundColor="white"
      borderRadius="8px"
      boxShadow="1px 4px 15px 0px #3030301F"
      width="100%"
      padding="40px"
      gap="20px"
    >
      <Text
        fontFamily="lato"
        fontSize="16px"
        fontWeight="700"
        color={colors.dark}
      >
        Media
      </Text>
      <Flex gap="10px" mb="10px">
        <select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
        >
          <option value="">Select Tag</option>
          <option value="LAB_RESULT">Lab Result</option>
          <option value="XRAY">X-Ray</option>
          <option value="ECG">Ecg</option>
          <option value="ECHO">Echo</option>
          <option value="USS">Ultrasound scan</option>
          <option value="CT_SCAN">CT Scan</option>
          <option value="MRI">MRI</option>
          <option value="ENDOSCOPY">Endoscopy</option>
          <option value="Lung_Function_Test">Lung Function Test</option>
          <option value="PRESCRIPTION">Prescription</option>
          <option value="CLINICAL_NOTE">Clinical Note</option>
          <option value="OTHERS">Others</option>
        </select>

        <Input
          placeholder="Optional label (e.g. Chest X-ray)"
          value={selectedLabel}
          onChange={(e) => setSelectedLabel(e.target.value)}
        />
      </Flex>
      <Grid templateColumns="repeat(3, 1fr)" gap={6}>
        {mediaFiles.map((media) => (
          <GridItem
            key={media.id}
            width="100%"
            height="100px"
            backgroundColor={colors.lightGrey}
            borderRadius="8px"
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            cursor="pointer"
            onClick={() => openMediaPreview(media)}
          >
            {media.url.endsWith(".jpg") ||
            media.url.endsWith(".png") ||
            media.url.endsWith(".jpeg") ? (
              <Image
                src={media.url}
                alt="uploaded media"
                objectFit="cover"
                width="100%"
                height="100%"
                borderRadius="8px"
              />
            ) : (
              <Text fontFamily="lato" fontSize="14px" color={colors.dark}>
                {media.label || media.tag || "Unknown File"}
              </Text>
            )}
            <IconButton
              icon={<CloseIcon />}
              size="sm"
              colorScheme="red"
              position="absolute"
              top="2"
              right="2"
              aria-label="Delete media"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteMedia(media.id);
              }}
            />
          </GridItem>
        ))}
        <GridItem>
          <IconButton
            as="label"
            htmlFor="file-upload"
            aria-label="Add Media"
            icon={<AddIcon />}
            size="lg"
            colorScheme="blue"
            borderRadius="8px"
            backgroundColor={colors.primary}
            color="white"
            _hover={{ backgroundColor: colors.dark }}
          />
          <Input
            id="file-upload"
            type="file"
            multiple
            display="none"
            onChange={handleFileUpload}
          />
        </GridItem>
      </Grid>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Media Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" justifyContent="center" alignItems="center">
            {selectedMedia?.file?.type?.startsWith("image/") ? (
              <Image
                src={selectedMedia.previewUrl}
                alt={selectedMedia.file.name}
                maxWidth="100%"
                maxHeight="80vh"
              />
            ) : selectedMedia?.file?.type === "application/pdf" ? (
              <Worker
                workerUrl={`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`}
              >
                <Viewer fileUrl={selectedMedia.previewUrl} />
              </Worker>
            ) : selectedMedia?.file?.name?.endsWith(".docx") ? (
              <Button
                as="a"
                href={selectedMedia.previewUrl}
                download={selectedMedia.file.name}
                colorScheme="blue"
              >
                Download {selectedMedia.file.name}
              </Button>
            ) : (
              <Text fontFamily="lato" fontSize="16px" color={colors.dark}>
                Unsupported file format
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default MediaCard;
